import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "style", "id", "arrow", "anchorId", "open", "position", "keepMounted", "offset", "focused", "children", "anchorEl", "popperOptions", "onClickOutside", "onEscape", "onKeyDown"];
import React, { useState, useEffect, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { usePopper } from 'react-popper';
import DropdownContainer from './DropdownContainer';
import DropdownHeader from './DropdownHeader';
import DropdownFooter from './DropdownFooter';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import { isFunction, positions, targetWithin } from '../helper';
import { useEventListener, useMergeRefs, useId } from '../hooks';
const ARROW_HEIGHT = '14px';
const ARROW_SHADOW_BLUR = '4px';
const StyledArrow = styled.span.withConfig({
  displayName: "Dropdown__StyledArrow",
  componentId: "sc-zn6euy-0"
})(["width:", ";height:", ";&:before{content:'';background-color:var(--neutral-surface);display:block;width:", ";height:", ";transform:rotate(45deg);box-shadow:0 0 ", " rgba(33,36,41,0.1);}&:after{content:'';position:absolute;background-color:var(--neutral-surface);}"], ARROW_HEIGHT, ARROW_HEIGHT, ARROW_HEIGHT, ARROW_HEIGHT, ARROW_SHADOW_BLUR);
const Root = styled.div.withConfig({
  displayName: "Dropdown__Root",
  componentId: "sc-zn6euy-1"
})(["", " display:none;min-width:160px;max-width:620px;padding:0;margin:0;border-radius:var(--border-radius-rounded);background-color:var(--neutral-surface);box-shadow:var(--shadow-md);will-change:transform;z-index:var(--zindex-1);position:relative;&:focus{outline:none;}", " &[data-popper-placement^='top'],&[data-popper-placement^='bottom']{", "{&:after{width:calc(100% + 2 * ", ");height:calc(50% + ", ");left:calc(-1 * ", ");}}}&[data-popper-placement^='right'],&[data-popper-placement^='left']{", "{&:after{width:calc(50% + ", ");height:calc(100% + 2 * ", ");top:calc(-1 * ", ");}}}&[data-popper-placement^='top']{", "{bottom:calc(", " / -2);&:after{bottom:50%;}}}&[data-popper-placement^='bottom']{", "{top:calc(", " / -2);&:after{top:50%;}}}&[data-popper-placement^='right']{", "{left:calc(", " / -2);&:after{left:50%;}}}&[data-popper-placement^='left']{", "{right:calc(", " / -2);&:after{right:50%;}}}"], ScopedCssBaseline, _ref => {
  let {
    open
  } = _ref;
  return open && css(["display:flex;flex-direction:column;"]);
}, StyledArrow, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, StyledArrow, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT);
global.ResizeObserver = global.ResizeObserver || class {
  observe() {}

  disconnect() {}

};
const Dropdown = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    className,
    style: styleProp,
    id: idProp,
    arrow,
    anchorId,
    open,
    position,
    keepMounted,
    offset,
    focused,
    children,
    anchorEl,
    popperOptions,
    onClickOutside,
    onEscape,
    onKeyDown
  } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded);

  const [popperElement, setPopperElement] = useState(null);
  const rootRef = useMergeRefs([setPopperElement, ref]);
  const [anchor, setAnchor] = useState(anchorEl);
  const [arrowRef, setArrowRef] = useState(null);
  const [mounted, setMounted] = useState(false);
  const id = useId(idProp);
  useEventListener('mousedown', event => {
    if (!open) return;
    const contains = targetWithin(event.target, [popperElement, anchor]);
    if (!contains && isFunction(onClickOutside)) onClickOutside(event);
  });

  const handleKeyDown = event => {
    if (!open) return;
    isFunction(onKeyDown) && onKeyDown(event);
    event.key === 'Escape' && isFunction(onEscape) && onEscape(event);
  };

  const offsetModifier = useMemo(() => ({
    name: 'offset',
    phase: 'beforeMain',
    enabled: true,
    options: {
      offset: [0, offset + parseInt(arrow ? ARROW_HEIGHT : 0)]
    }
  }), [offset, arrow]);
  const focusModifier = useMemo(() => ({
    name: 'focus',
    phase: 'afterWrite',
    enabled: true,
    fn: _ref3 => {
      let {
        state
      } = _ref3;
      return focused && state.elements.popper.focus();
    }
  }), [focused]);
  const resizeModifier = useMemo(() => ({
    name: 'resize',
    phase: 'afterWrite',
    enabled: true,
    effect: _ref4 => {
      let {
        instance,
        state
      } = _ref4;
      const observer = new global.ResizeObserver(() => instance.update());
      observer.observe(state.elements.reference);
      return () => observer.disconnect();
    }
  }), []);
  const arrowModifier = useMemo(() => ({
    name: 'arrow',
    phase: 'main',
    options: {
      element: arrowRef
    }
  }), [arrowRef]);
  const {
    styles,
    attributes
  } = usePopper(anchor, popperElement, Object.assign({
    placement: position
  }, popperOptions, {
    modifiers: [offsetModifier, focusModifier, resizeModifier, arrowModifier]
  }));
  const style = Object.assign({}, styles.popper, styleProp);
  useEffect(() => {
    anchorEl && setAnchor(anchorEl);
  }, [anchorEl]);
  useEffect(() => {
    if (keepMounted && open && !mounted) setMounted(true);
  }, [open, keepMounted, mounted]);
  useEffect(() => {
    if (keepMounted) {
      open && (anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.setAttribute('aria-owns', id));
    } else {
      open ? anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.setAttribute('aria-owns', id) : anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.removeAttribute('aria-owns');
    }

    anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.setAttribute('aria-expanded', open);
  }, [keepMounted, anchorEl, id, open]);
  return (mounted || open) && /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    id: id,
    className: className,
    style: style,
    ref: rootRef,
    tabIndex: open ? 0 : -1,
    onKeyDown: handleKeyDown,
    "aria-labelledby": anchorId || (anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.id),
    open: open
  }, attributes.popper), arrow && /*#__PURE__*/React.createElement(StyledArrow, {
    style: styles.arrow,
    ref: setArrowRef
  }), children);
});
Dropdown.displayName = 'Dropdown';
Dropdown.Style = Root;
Dropdown.Container = DropdownContainer;
Dropdown.Header = DropdownHeader;
Dropdown.Footer = DropdownFooter;
Dropdown.positions = {
  top: positions.top,
  topStart: positions.topStart,
  topEnd: positions.topEnd,
  bottom: positions.bottom,
  bottomStart: positions.bottomStart,
  bottomEnd: positions.bottomEnd,
  left: positions.left,
  leftStart: positions.leftStart,
  leftEnd: positions.leftEnd,
  right: positions.right,
  rightStart: positions.rightStart,
  rightEnd: positions.rightEnd
};
Dropdown.defaultProps = {
  arrow: false,
  offset: 4,
  keepMounted: false,
  focused: true,
  open: false,
  position: Dropdown.positions.bottomStart,
  popperOptions: {}
};
process.env.NODE_ENV !== "production" ? Dropdown.propTypes = {
  /**
   * Sets the children elements, which usually contains `Dropdown.Container`, `Dropdown.Header` and `Dropdown.Footer`
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,

  /**
   * Sets the id used to connect accessibly `Dropdown` with anchor element. The id value is used in `aria-labelledby`.
   */
  anchorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * @ignore
   * A boolean value to control if render with arrow.
   */
  arrow: PropTypes.bool,

  /**
   * Sets the anchor element (html element).
   */
  anchorEl: PropTypes.instanceOf(Element),

  /**
   * A boolean value to control if render opened or closed.
   */
  open: PropTypes.bool,

  /**
   * Determines the position of dropdown related at anchorEl.
   */
  position: PropTypes.oneOf([Dropdown.positions.top, Dropdown.positions.topStart, Dropdown.positions.topEnd, Dropdown.positions.bottom, Dropdown.positions.bottomStart, Dropdown.positions.bottomEnd, Dropdown.positions.left, Dropdown.positions.leftStart, Dropdown.positions.leftEnd, Dropdown.positions.right, Dropdown.positions.rightStart, Dropdown.positions.rightEnd]),

  /**
   * Sets additional distance between dropdown and trigger using number to represent pixels.
   */
  offset: PropTypes.number,

  /**
   * Callback triggered when the `Dropdown` is open and click outside of dropdown.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onClickOutside: PropTypes.func,

  /**
   * Callback triggered when the `Dropdown` is opened and ESC key is pressed.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onEscape: PropTypes.func,

  /**
   * Callback triggered when the `Dropdown` is focused and opened.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onKeyDown: PropTypes.func,

  /**
   * Options provided to the [`popper.js`](https://popper.js.org/docs/v1/) instance.
   */
  popperOptions: PropTypes.object,

  /**
   * Sets if Dropdown will mount with focus or not.
   */
  focused: PropTypes.bool,

  /**
   * Sets if the `Dropdown` will be always mounted after the first mount. It can be useful for SEO.
   */
  keepMounted: PropTypes.bool
} : void 0;
export default Dropdown;